/**
 * GovKanban — Kanban de Obras Governamentais (licitações).
 *
 * 6 colunas: NEW → HABILITACAO → ANALYZING → BIDDING → AWARDED → LOST.
 * Cards especializados em licitação: contador de dias p/ deadline, valor
 * estimado em destaque, tags de materiais do edital, tag "Match de Produto"
 * (+ distância da sede), timestamp de verificação e nota tática da Claire.
 * Drawer: Checklist de Certidões (Claire) + notas internas.
 */
import ClaireHelpButton from "../components/ClaireHelpButton";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2, Calendar, Loader2, RefreshCw, Trash2, ExternalLink,
  AlertTriangle, Award, Clock, Search, Save, ListChecks,
  DollarSign, MapPin, Mail, Phone, FileText, Hash, Briefcase,
  Sparkles, Target, ShieldCheck, ChevronDown, Package, CheckCircle2,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import { EmailAction, PhoneAction } from "../components/ContactActions";
import WinningStrategy from "../components/WinningStrategy";
import Logo from "../components/Logo";
import LangToggle from "../components/LangToggle";
import ThemeToggle from "../components/ThemeToggle";
import { useNavigate, useSearchParams } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ["NEW", "HABILITACAO", "ANALYZING", "BIDDING", "AWARDED", "LOST"];
const STATUS_META = {
  NEW:         { color: "#3B82F6", label: "Novos", icon: ListChecks },
  HABILITACAO: { color: "#0EA5E9", label: "Habilitação Técnica", icon: ShieldCheck },
  ANALYZING:   { color: "#A855F7", label: "Analisando", icon: Search },
  BIDDING:     { color: "#F59E0B", label: "Em proposta", icon: Save },
  AWARDED:     { color: "#10B981", label: "Ganhos", icon: Award },
  LOST:        { color: "#EF4444", label: "Perdidos", icon: AlertTriangle },
};

const daysLeft = (iso) => {
  if (!iso) return null;
  const d = Math.ceil((new Date(iso) - new Date()) / 86400000);
  return Number.isNaN(d) ? null : d;
};
const fmtMoney = (v) => {
  const n = Number(v || 0);
  if (!n) return null;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
};
const daysSince = (iso) => iso ? Math.floor((new Date() - new Date(iso)) / 86400000) : null;

export default function GovKanban() {
  const [board, setBoard] = useState({ columns: {}, totals: {} });
  const [loading, setLoading] = useState(true);
  const [draggingId, setDraggingId] = useState(null);
  const [openCard, setOpenCard] = useState(null);
  const [aiMenuOpen, setAiMenuOpen] = useState(false);
  const [matching, setMatching] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [highlightId, setHighlightId] = useState(null);
  const highlightDone = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/gov-leads/board`);
      setBoard(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Deep link from the Intelligence Feed: /gov-kanban?leadId=<id> —
  // auto-open the detail drawer, scroll the card to center and glow it for 5s.
  useEffect(() => {
    const leadId = searchParams.get("leadId");
    if (!leadId || highlightDone.current || loading) return;
    const card = Object.values(board.columns || {}).flat().find((c) => c.id === leadId);
    if (!card) return;
    highlightDone.current = true;
    setHighlightId(leadId);
    setOpenCard(card);
    setTimeout(() => {
      document.querySelector(`[data-testid="gov-card-${leadId}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
    }, 200);
    setTimeout(() => {
      setHighlightId(null);
      setSearchParams({}, { replace: true });
    }, 5200);
  }, [board, loading, searchParams, setSearchParams]);

  useEffect(() => {
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setAiMenuOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const totalCount = useMemo(
    () => Object.values(board.totals || {}).reduce((s, v) => s + (v || 0), 0),
    [board.totals],
  );

  const moveCard = async (id, toStatus) => {
    try {
      await axios.post(`${API}/gov-leads/${id}/move`, { status: toStatus });
      toast.success(`Movido para ${STATUS_META[toStatus]?.label || toStatus}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  const removeCard = async (id) => {
    if (!window.confirm("Remover este lead governamental?")) return;
    try {
      await axios.delete(`${API}/gov-leads/${id}`);
      toast.success("Removido");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  const runMatching = async () => {
    setMatching(true); setAiMenuOpen(false);
    try {
      const { data } = await axios.post(`${API}/product-catalog/match-all`);
      if (data.ok) {
        toast.success(`Match de Produto: ${data.matched}/${data.scanned} licitações (${data.perfect} perfeitos)`);
        await load();
      } else {
        toast.info("Cadastre seus produtos no Catálogo primeiro");
        navigate("/product-catalog");
      }
    } catch (_e) { toast.error("Falha no matching"); }
    finally { setMatching(false); }
  };

  return (
    <div className="min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]" data-testid="gov-kanban-page">
      <Sidebar />
      <div className="ml-[var(--al-sidebar-w,260px)] flex flex-col min-w-0">
        <header className="h-14 border-b border-[var(--al-border)] flex items-center justify-between px-6 sticky top-0 z-20 bg-[var(--al-bg)]">
          <div className="flex items-center gap-3">
            <Logo />
            <span className="hidden sm:inline-flex items-center gap-1.5 text-[12px] text-[var(--al-text-muted)] font-mono-tech">
              <Building2 className="w-3.5 h-3.5 text-[#C9A24E]" /> Gov · CRM
            </span>
          </div>
          <div className="flex items-center gap-3">
            {/* AI Power Tools dropdown — keeps the toolbar focused */}
            <div className="relative" ref={menuRef}>
              <button type="button" data-testid="gov-ai-tools-btn"
                onClick={() => setAiMenuOpen((v) => !v)}
                className="h-9 px-3 rounded-lg bg-gradient-to-r from-[#1B3A8A] to-[#7C3AED] text-white text-[12px] font-bold inline-flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" /> AI Power Tools
                <ChevronDown className="w-3 h-3" />
              </button>
              {aiMenuOpen && (
                <div className="absolute right-0 top-11 w-64 rounded-xl border border-[var(--al-border)] bg-[var(--al-card-bg)] shadow-xl p-1.5 z-30"
                  data-testid="gov-ai-tools-menu">
                  <button type="button" data-testid="gov-ai-match"
                    onClick={runMatching} disabled={matching}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-[var(--al-bg)] text-[12.5px] inline-flex items-center gap-2">
                    {matching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Target className="w-3.5 h-3.5 text-emerald-500" />}
                    Aplicar Match de Produto
                  </button>
                  <button type="button" data-testid="gov-ai-catalog"
                    onClick={() => navigate("/product-catalog")}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-[var(--al-bg)] text-[12.5px] inline-flex items-center gap-2">
                    <Package className="w-3.5 h-3.5 text-[#C9A24E]" />
                    Catálogo de Produtos
                  </button>
                  <div className="px-3 py-2">
                    <ClaireHelpButton testid="gov-kanban"
                      question="Como uso o CRM de Obras Governamentais — mover oportunidades, checklist de certidões e match de produto?" />
                  </div>
                </div>
              )}
            </div>
            <button onClick={load} disabled={loading}
              data-testid="gov-kanban-refresh"
              className="h-9 px-3 rounded-lg border border-[var(--al-border)] text-[12px] font-bold inline-flex items-center gap-1.5 hover:bg-[var(--al-card-bg)]">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Atualizar
            </button>
            <ThemeToggle />
            <LangToggle />
          </div>
        </header>

        <main className="flex-1 p-6">
          <div className="mb-5">
            <h1 className="text-[20px] font-bold flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#C9A24E]" /> CRM · Obras Governamentais
            </h1>
            <p className="text-[12.5px] text-[var(--al-text-muted)] mt-1 max-w-2xl">
              Pipeline de licitações públicas. Cards com <b>🎯 Match de Produto</b> batem com o seu
              <button type="button" onClick={() => navigate("/product-catalog")} className="text-[#C9A24E] hover:underline font-bold mx-1">Catálogo de Produtos</button>.
              Total: <b>{totalCount}</b>.
            </p>
          </div>

          {loading && totalCount === 0 ? (
            <div className="py-20 text-center text-[var(--al-text-muted)] inline-flex items-center justify-center gap-2 w-full">
              <Loader2 className="w-5 h-5 animate-spin" /> Carregando pipeline…
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="gov-kanban-columns">
              {STATUSES.map((st) => {
                const meta = STATUS_META[st];
                const Icon = meta.icon;
                const cards = (board.columns?.[st]) || [];
                return (
                  <div key={st}
                    data-testid={`gov-col-${st}`}
                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("ring-2"); }}
                    onDragLeave={(e) => e.currentTarget.classList.remove("ring-2")}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove("ring-2");
                      if (draggingId) moveCard(draggingId, st);
                      setDraggingId(null);
                    }}
                    className="rounded-xl border bg-[var(--al-card-bg)] p-3 min-h-[200px] transition-all"
                    style={{ borderColor: "var(--al-border)", boxShadow: `0 0 0 1px ${meta.color}22 inset` }}>
                    <div className="flex items-center gap-2 mb-3 px-1">
                      <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: meta.color }} />
                      <h3 className="text-[11px] font-bold uppercase tracking-wide truncate" style={{ color: meta.color }}>{meta.label}</h3>
                      <span className="ml-auto text-[10.5px] font-mono-tech px-2 py-0.5 rounded"
                        style={{ background: `${meta.color}1A`, color: meta.color }}
                        data-testid={`gov-count-${st}`}>{cards.length}</span>
                    </div>
                    <div className="space-y-2">
                      {cards.map((c) => (
                        <GovCard key={c.id} card={c}
                          highlight={highlightId === c.id}
                          onDragStart={() => setDraggingId(c.id)}
                          onDragEnd={() => setDraggingId(null)}
                          onMove={moveCard}
                          onDelete={removeCard}
                          onOpen={() => setOpenCard(c)} />
                      ))}
                      {cards.length === 0 && (
                        <div className="text-[11.5px] text-[var(--al-text-muted)] text-center py-6">
                          Nenhum item
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </div>

      {openCard && <NotesDrawer card={openCard} onClose={() => setOpenCard(null)} onSaved={load} />}
    </div>
  );
}

function GovCard({ card, onDragStart, onDragEnd, onMove, onDelete, onOpen, highlight }) {
  const meta = STATUS_META[card.status] || STATUS_META.NEW;
  const dl = daysLeft(card.deadline);
  const value = fmtMoney(card.estimated_value);
  const pm = card.product_match;
  const verifiedDays = daysSince(card.updated_at || card.created_at);
  const verFresh = verifiedDays != null && verifiedDays <= 30;
  const materials = (card.materials || card.tags || []).slice(0, 3);
  const dlClass = dl == null ? "" : dl < 0 ? "bg-gray-200 text-gray-500"
    : dl <= 3 ? "bg-rose-100 text-rose-700"
    : dl <= 10 ? "bg-amber-100 text-amber-700"
    : "bg-emerald-100 text-emerald-700";
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onOpen}
      data-testid={`gov-card-${card.id}`}
      className={`rounded-lg border bg-[var(--al-bg)] p-3 cursor-grab active:cursor-grabbing transition-colors ${
        highlight ? "al-feed-glow" : pm?.perfect ? "border-[#C9A24E] shadow-[0_0_0_1px_#C9A24E44]" : "border-[var(--al-border)] hover:border-[#C9A24E]/60"}`}
    >
      {highlight && (
        <span data-testid={`gov-feed-badge-${card.id}`}
          className="al-feed-badge mb-1.5 inline-flex items-center gap-1 text-[9px] font-extrabold uppercase tracking-wide px-1.5 py-0.5 rounded bg-gradient-to-r from-[#8B5CF6] to-[#C9A24E] text-white">
          ✨ Vindo do Feed
        </span>
      )}
      {/* Claire's Victory Index — progressive color bar (red/yellow/green) */}
      {card.victory_index != null && (
        <div className="mb-1.5" data-testid={`gov-victory-${card.id}`}
          title={`Índice de Vitória da Claire: ${card.victory_index}/10 · Produto ${card.victory_parts?.product ?? "—"}/4 · Proximidade ${card.victory_parts?.proximity ?? "—"}/3 · Histórico GC ${card.victory_parts?.gc_history ?? "—"}/3`}>
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] font-extrabold uppercase tracking-wide text-[#8B5CF6]">⭐ Vitória</span>
            <div className="flex-1 h-1.5 bg-[var(--al-border)] rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all"
                style={{
                  width: `${(card.victory_index / 10) * 100}%`,
                  background: card.victory_index >= 7 ? "#22C55E" : card.victory_index >= 4 ? "#F59E0B" : "#EF4444",
                }} />
            </div>
            <span className="text-[10px] font-extrabold tabular-nums"
              style={{ color: card.victory_index >= 7 ? "#22C55E" : card.victory_index >= 4 ? "#F59E0B" : "#EF4444" }}>
              {card.victory_index}/10
            </span>
          </div>
        </div>
      )}

      {/* Match de Produto highlight */}
      {pm && (
        <div className="mb-1.5 inline-flex flex-wrap items-center gap-1" data-testid={`gov-match-${card.id}`}>
          <span className="text-[9.5px] font-extrabold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#C9A24E] text-[#0A1233] inline-flex items-center gap-1">
            <Target className="w-2.5 h-2.5" /> Match de Produto{pm.perfect ? " ✦" : ""}
          </span>
          {pm.distance_mi != null && (
            <span className="text-[9.5px] font-bold px-1.5 py-0.5 rounded bg-[#3B82F6]/10 text-[#3B82F6]">
              🚚 {pm.distance_mi} mi
            </span>
          )}
        </div>
      )}

      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="text-[12.5px] font-bold leading-snug line-clamp-2 flex-1">{card.title}</div>
        {card.fit_score > 0 && (
          <span className="shrink-0 text-[10px] font-mono-tech px-1.5 py-0.5 rounded"
            style={{ background: `${meta.color}1A`, color: meta.color }}>{card.fit_score}</span>
        )}
      </div>
      <div className="text-[11px] text-[var(--al-text-muted)] flex items-center gap-1.5 mb-1.5 truncate">
        {card.agency || "—"}{card.city ? ` · ${card.city}, ${card.state}` : (card.state ? ` · ${card.state}` : "")}
      </div>

      {/* Countdown + value row */}
      <div className="flex items-center flex-wrap gap-1.5 mb-1.5">
        {dl != null && (
          <span className={`text-[10px] font-extrabold px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${dlClass}`}
            data-testid={`gov-countdown-${card.id}`}>
            <Clock className="w-2.5 h-2.5" />
            {dl < 0 ? "Encerrado" : dl === 0 ? "Hoje!" : `${dl}d restantes`}
          </span>
        )}
        {value && (
          <span className="text-[11px] font-extrabold text-[#1B3A8A] dark:text-[#8FB3FF] inline-flex items-center gap-0.5"
            data-testid={`gov-value-${card.id}`}>
            <DollarSign className="w-3 h-3" />{value.replace("$", "")}
          </span>
        )}
      </div>

      {/* Material tags */}
      {materials.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5" data-testid={`gov-materials-${card.id}`}>
          {materials.map((m) => (
            <span key={m} className="text-[9.5px] px-1.5 py-0.5 rounded bg-[var(--al-card-bg)] border border-[var(--al-border)]">{m}</span>
          ))}
        </div>
      )}

      {/* Claire AI note */}
      {card.ai_note && (
        <div className="text-[10.5px] leading-snug bg-violet-500/8 border border-violet-500/25 rounded p-1.5 mb-1.5 line-clamp-3"
          style={{ background: "rgba(139,92,246,0.08)" }} data-testid={`gov-ainote-${card.id}`}>
          <Sparkles className="w-2.5 h-2.5 inline mr-1 text-violet-500" />{card.ai_note}
        </div>
      )}

      {/* Verified timestamp */}
      {verifiedDays != null && (
        <div className="text-[9.5px] inline-flex items-center gap-1 mb-1"
          style={{ color: verFresh ? "#10B981" : "#F59E0B" }}
          title={verFresh ? "Dados verificados há menos de 30 dias" : "Dados com mais de 30 dias — considere re-verificar"}
          data-testid={`gov-verified-${card.id}`}>
          <CheckCircle2 className="w-2.5 h-2.5" />
          verificado há {verifiedDays}d
        </div>
      )}

      <div className="mt-2 pt-2 border-t border-[var(--al-border)] flex items-center gap-1.5">
        <select
          value={card.status}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onMove(card.id, e.target.value)}
          data-testid={`gov-status-select-${card.id}`}
          className="flex-1 min-w-0 text-[10.5px] border border-[var(--al-border)] rounded px-1.5 py-1 bg-[var(--al-card-bg)]"
        >
          {STATUSES.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
        </select>
        {card.source_url && (
          <a href={card.source_url} target="_blank" rel="noreferrer"
             onClick={(e) => e.stopPropagation()}
             className="p-1.5 rounded text-[var(--al-text-muted)] hover:text-[#C9A24E]"
             title="Abrir fonte">
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
        <button onClick={(e) => { e.stopPropagation(); onDelete(card.id); }}
          data-testid={`gov-delete-${card.id}`}
          className="p-1.5 rounded text-rose-400 hover:bg-rose-500/10"
          title="Remover este lead">
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

function NotesDrawer({ card, onClose, onSaved }) {
  const [notes, setNotes] = useState(card.notes || "");
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(card);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [checklist, setChecklist] = useState(card.cert_checklist || null);
  const [certLoading, setCertLoading] = useState(false);
  const [noteLoading, setNoteLoading] = useState(false);
  const [aiNote, setAiNote] = useState(card.ai_note || "");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingDetail(true);
      try {
        const { data } = await axios.get(`${API}/gov-leads/${card.id}`);
        if (!cancelled) {
          setDetail({ ...card, ...data });
          if (data.cert_checklist) setChecklist(data.cert_checklist);
          if (data.ai_note) setAiNote(data.ai_note);
        }
      } catch (_e) { /* keep summary on failure */ }
      finally { if (!cancelled) setLoadingDetail(false); }
    })();
    return () => { cancelled = true; };
  }, [card.id]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/gov-leads/${card.id}`, { notes });
      toast.success("Notas salvas");
      onClose();
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally { setSaving(false); }
  };

  const genChecklist = async () => {
    setCertLoading(true);
    try {
      const { data } = await axios.post(`${API}/gov-leads/${card.id}/cert-checklist`);
      setChecklist(data.checklist);
      toast.success("Checklist gerado pela Claire");
    } catch (_e) { toast.error("Falha ao gerar checklist"); }
    finally { setCertLoading(false); }
  };

  const toggleCert = async (index, has) => {
    const next = checklist.map((c, i) => i === index ? { ...c, has } : c);
    setChecklist(next);
    try {
      await axios.patch(`${API}/gov-leads/${card.id}/cert-checklist`, { index, has });
    } catch (_e) { toast.error("Falha ao salvar"); }
  };

  const genNote = async () => {
    setNoteLoading(true);
    try {
      const { data } = await axios.post(`${API}/gov-leads/${card.id}/ai-note`);
      setAiNote(data.ai_note);
      toast.success("Nota da Claire gerada");
      onSaved?.();
    } catch (_e) { toast.error("Claire indisponível"); }
    finally { setNoteLoading(false); }
  };

  const fmtUsd = (v) => v ? `US$ ${Number(v).toLocaleString("en-US")}` : "—";
  const sourceUrl = detail.source_url || detail.external_url || "";
  const fullDesc = detail.description || detail.synopsis || "";
  const dl = daysLeft(detail.deadline);
  const doneCount = (checklist || []).filter((c) => c.has).length;

  return (
    <div className="fixed inset-0 z-50 bg-black/55 flex items-end sm:items-start justify-center sm:p-4 overflow-y-auto"
         data-testid="gov-card-drawer" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="rounded-t-2xl sm:rounded-xl p-4 sm:p-6 w-full sm:max-w-2xl border border-[var(--al-border)] sm:my-8 max-h-[92vh] sm:max-h-[90vh] overflow-y-auto"
           style={{
             backgroundColor: "var(--al-card-bg, #FFFFFF)",
             paddingBottom: "calc(1rem + env(safe-area-inset-bottom, 0px))",
           }}>
        <div className="flex items-start gap-2 mb-2">
          <Building2 className="w-5 h-5 text-[#C9A24E] mt-1 shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="text-[16px] font-bold leading-snug">{detail.title}</h3>
            <div className="text-[12px] text-[var(--al-text-muted)] mt-1">{detail.agency || "—"}</div>
          </div>
          <button onClick={onClose}
                  data-testid="gov-drawer-close"
                  className="w-10 h-10 rounded-full inline-flex items-center justify-center text-[var(--al-text-muted)] hover:text-rose-500 hover:bg-[var(--al-bg)] text-[22px] leading-none -mt-1 -mr-1">×</button>
        </div>

        {dl != null && dl >= 0 && (
          <div className={`mb-3 text-[12px] font-bold px-3 py-2 rounded-lg inline-flex items-center gap-1.5 ${
            dl <= 3 ? "bg-rose-100 text-rose-700" : dl <= 10 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
            <Clock className="w-3.5 h-3.5" /> {dl === 0 ? "Prazo final HOJE" : `${dl} dias até o prazo final`}
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 my-4 p-3 rounded-lg bg-[var(--al-row-alt)] border border-[var(--al-border)]">
          <DetailField icon={DollarSign} label="Valor estimado" value={fmtUsd(detail.estimated_value)} />
          <DetailField icon={Hash} label="NAICS" value={detail.naics_code || "—"} />
          <DetailField icon={MapPin} label="Local"
                       value={detail.city ? `${detail.city}, ${detail.state}` : (detail.state || "—")} />
          <DetailField icon={Calendar} label="Postado"
                       value={detail.posted_at ? detail.posted_at.slice(0, 10) : "—"} />
          <DetailField icon={Clock} label="Prazo"
                       value={detail.deadline ? detail.deadline.slice(0, 10) : "—"} />
          <DetailField icon={Briefcase} label="Tipo"
                       value={detail.opportunity_type || detail.set_aside || "—"} />
        </div>

        {/* Claire's Winning Strategy — senior consultant analysis (10 credits, persists) */}
        <div className="mb-4">
          <WinningStrategy leadType="gov" leadId={detail.id} isPt />
        </div>

        {/* Claire — Checklist de Certidões */}
        <div className="mb-4 p-3 rounded-lg border border-sky-500/30 bg-sky-500/5" data-testid="gov-cert-section">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-[10.5px] uppercase tracking-wide font-bold text-sky-500 inline-flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> Checklist de Certidões
              {checklist && <span className="normal-case font-semibold">— {doneCount}/{checklist.length} ✓</span>}
            </div>
            <button type="button" data-testid="gov-cert-btn" onClick={genChecklist} disabled={certLoading}
              className="h-8 px-2.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-[11px] font-bold inline-flex items-center gap-1 disabled:opacity-60">
              {certLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              {checklist ? "Regenerar" : "Gerar com a Claire"}
            </button>
          </div>
          {checklist ? (
            <div className="space-y-1.5">
              {checklist.map((c, i) => (
                <label key={`cert-${i}`} className="flex items-start gap-2 cursor-pointer text-[12px]"
                  data-testid={`gov-cert-item-${i}`}>
                  <input type="checkbox" checked={!!c.has}
                    onChange={(e) => toggleCert(i, e.target.checked)}
                    className="accent-sky-500 w-3.5 h-3.5 mt-0.5" />
                  <span className={c.has ? "line-through opacity-60" : ""}>
                    <b>{c.item}</b>
                    {c.why && <span className="text-[var(--al-text-muted)]"> — {c.why}</span>}
                  </span>
                </label>
              ))}
            </div>
          ) : (
            <p className="text-[11.5px] text-[var(--al-text-muted)] italic">
              A Claire lista quais certidões e habilitações este edital exige — marque as que você já possui.
            </p>
          )}
        </div>

        {/* Claire — nota tática */}
        <div className="mb-4 p-3 rounded-lg border border-violet-500/30" style={{ background: "rgba(139,92,246,0.06)" }}>
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <div className="text-[10.5px] uppercase tracking-wide font-bold text-violet-500 inline-flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Nota da Claire
            </div>
            <button type="button" data-testid="gov-ainote-btn" onClick={genNote} disabled={noteLoading}
              className="h-8 px-2.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-[11px] font-bold inline-flex items-center gap-1 disabled:opacity-60">
              {noteLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              {aiNote ? "Regenerar" : "Gerar nota"}
            </button>
          </div>
          <p className="text-[12.5px] leading-relaxed" data-testid={aiNote ? "gov-ainote-text" : "gov-ainote-empty"}>
            {aiNote || <span className="italic text-[var(--al-text-muted)]">Nota tática da IA aparece aqui e no card do Kanban.</span>}
          </p>
        </div>

        {(detail.contact_name || detail.contact_email || detail.contact_phone) && (
          <div className="mb-4 p-3 rounded-lg border border-[var(--al-border)]">
            <div className="text-[10.5px] uppercase tracking-wide font-bold text-[#C9A24E] mb-2">Contato</div>
            <div className="grid sm:grid-cols-2 gap-2 text-[12.5px]">
              {detail.contact_name && (
                <div className="flex items-center gap-1.5"><Briefcase className="w-3 h-3 text-[var(--al-text-muted)]" /> {detail.contact_name}</div>
              )}
              {detail.contact_email && (
                <div className="flex items-center gap-1.5">
                  <Mail className="w-3 h-3 text-[#C9A24E]" />
                  <EmailAction email={detail.contact_email} isPt testid="gov-contact-email"
                    context={{ name: detail.contact_name, opportunity: detail.title, agency: detail.agency,
                      city: detail.city, state: detail.state, value: detail.estimated_value,
                      description: (detail.description || detail.synopsis || "").slice(0, 500) }} />
                </div>
              )}
              {detail.contact_phone && (
                <PhoneAction phone={detail.contact_phone} isPt testid="gov-contact-phone" />
              )}
            </div>
          </div>
        )}

        {fullDesc && (
          <div className="mb-4">
            <div className="text-[10.5px] uppercase tracking-wide font-bold text-[#C9A24E] mb-1.5 flex items-center gap-1.5">
              <FileText className="w-3 h-3" /> Descrição
              {loadingDetail && <Loader2 className="w-3 h-3 animate-spin opacity-60" />}
            </div>
            <p className="text-[12.5px] leading-relaxed whitespace-pre-wrap max-h-[200px] overflow-y-auto">
              {fullDesc}
            </p>
          </div>
        )}

        {sourceUrl && (
          <a href={sourceUrl} target="_blank" rel="noopener noreferrer"
             data-testid="gov-drawer-source"
             className="inline-flex items-center gap-1.5 text-[12.5px] text-[#C9A24E] hover:underline font-bold mb-3">
            <ExternalLink className="w-3 h-3" /> Abrir no portal oficial (SAM.gov)
          </a>
        )}

        <label className="block text-[11px] uppercase tracking-wider text-[var(--al-text-muted)] font-bold mb-1 mt-2">Notas internas</label>
        <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)}
          data-testid="gov-notes-input"
          placeholder="Anotações sobre esta licitação, contato, próximos passos…"
          className="w-full bg-[var(--al-bg)] border border-[var(--al-border)] rounded p-2 text-[12.5px] focus:border-[#C9A24E] outline-none" />
        <div className="flex gap-2 mt-3">
          <button onClick={save} disabled={saving}
            data-testid="gov-notes-save"
            className="flex-1 h-9 rounded-lg bg-[#C9A24E] hover:bg-[#b48d3e] text-[#1A0E36] font-bold text-[12.5px] inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Salvar notas
          </button>
          <button onClick={onClose}
            className="px-4 h-9 rounded-lg border border-[var(--al-border)] text-[12.5px] font-bold">
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailField({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="text-[9.5px] uppercase tracking-wider text-[var(--al-text-muted)] font-bold mb-0.5 flex items-center gap-1">
        <Icon className="w-2.5 h-2.5" /> {label}
      </div>
      <div className="text-[12.5px] font-semibold truncate" title={value}>{value}</div>
    </div>
  );
}
